import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProgress } from '../context/ProgressContext';
import UpgradeOptions from './UpgradeOptions';
import { getSessionId } from '../utils/session';

// Lazy load role-specific menu components
const ProducerMenu = lazy(() => import('./menus/ProducerMenu'));
const HubMenu = lazy(() => import('./menus/HubMenu'));
const PickupMenu = lazy(() => import('./menus/PickupMenu'));
const EducatorMenu = lazy(() => import('./menus/EducatorMenu'));
const AdminMenu = lazy(() => import('./menus/AdminMenu'));
const KinMenu = lazy(() => import('./menus/KinMenu'));
const BuyerMenu = lazy(() => import('./menus/BuyerMenu'));

const MenuLoader = () => (
  <div className="px-3 py-2 text-sm text-gray-400">Loading...</div>
);

// Role icons and names
const ROLE_ICONS = {
  buyer: '🛒',
  producer: '🚜',
  hub: '🏪',
  pickup: '📦',
  educator: '🎓',
  admin: '⚙️',
  kin_coordinator: '🌟',
  kin_member: '👥',
  curious: '🔍'
};

const ROLE_NAMES = {
  buyer: 'Buyer',
  producer: 'Producer',
  hub: 'Hub',
  pickup: 'Pickup',
  educator: 'Educator',
  admin: 'Admin',
  kin_coordinator: 'Kin Coordinator',
  kin_member: 'Kin Member',
  curious: 'Curious'
};

// Lazy-loaded menu components
const MenuComponents = {
  producer: ProducerMenu,
  hub: HubMenu,
  pickup: PickupMenu,
  educator: EducatorMenu,
  admin: AdminMenu,
  kin_coordinator: KinMenu,
  kin_member: KinMenu,
  buyer: BuyerMenu
};

// Dashboard path mapping
const DASHBOARD_PATHS = {
  producer: '/producer-dashboard',
  hub: '/hub-dashboard',
  pickup: '/pickup-dashboard',
  educator: '/educator-dashboard',
  admin: '/admin',
  buyer: '/buyer/dashboard',
  kin_coordinator: '/kin-dashboard',
  kin_member: '/kin-dashboard'
};

// Helper to get unread message count
const fetchUnreadCount = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return 0;
    const res = await fetch('/api/favorites/communication/unread/count', {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (res.ok) {
      const data = await res.json();
      return data.count || 0;
    }
    return 0;
  } catch (error) {
    console.error('Error fetching unread count:', error);
    return 0;
  }
};

  const UserStatus = ({ user, availableRoles }) => {
  const { logout, switchRole } = useAuth();
  const { credits, modulesCompleted, loading, dataReady, fetchProgress } = useProgress();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Get the active role from localStorage or fallback to user.role
  const [activeRole, setActiveRole] = useState(() => {
    const stored = localStorage.getItem('selectedRole');
    if (stored && availableRoles?.includes(stored)) return stored;
    return user?.role || 'buyer';
  });

  // Fetch unread count periodically
useEffect(() => {
  if (!user || !user._id) return;

  let interval;

  const fetchCount = () => {
    // Only fetch if page is visible
    if (document.visibilityState === 'visible') {
      fetchUnreadCount().then(setUnreadCount);
    }
  };

  // Initial fetch
  fetchCount();

  // Poll every 5 minutes (300000ms) instead of 30 seconds
  interval = setInterval(fetchCount, 300000);

  // Refetch when the page becomes visible again
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      fetchCount();
    }
  };
  document.addEventListener('visibilitychange', handleVisibilityChange);

  return () => {
    clearInterval(interval);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
}, [user]);

  // Update activeRole when availableRoles or user changes
  useEffect(() => {
    const stored = localStorage.getItem('selectedRole');
    
    // Check if user has Kin Coordinator role - make it the default
    const hasCoordinator = availableRoles?.includes('kin_coordinator');
    
    if (hasCoordinator) {
      // If user is a Kin Coordinator, make that the default
      setActiveRole('kin_coordinator');
      localStorage.setItem('selectedRole', 'kin_coordinator');
      console.log('🌟 Defaulting to Kin Coordinator role');
    } else if (stored && availableRoles?.includes(stored)) {
      setActiveRole(stored);
    } else if (user?.role && availableRoles?.includes(user.role)) {
      setActiveRole(user.role);
    } else if (availableRoles?.length > 0) {
      // Default to first available role (usually 'buyer')
      setActiveRole(availableRoles[0]);
    }
  }, [availableRoles, user]);

  console.log('🔍 UserStatus - credits:', credits, 'modulesCompleted:', modulesCompleted);
  console.log('🔍 UserStatus - availableRoles:', availableRoles, 'activeRole:', activeRole);

  // Progressive status checks
  // Progressive status checks
  const isLoggedIn = user && user._id;
  // Check if there's a session ID - indicates the user has visited or started a Learn module
  const sessionId = localStorage.getItem('ourpickup_session');
  const hasSession = !!sessionId;
  const isCuriousStarted = localStorage.getItem('curious_started') === 'true';
  const hasCompletedModule = localStorage.getItem('curious_status') === 'active';
  const hasEmail = localStorage.getItem('curious_email') !== null;
  const isCuriousPlus = hasCompletedModule && hasEmail;
  // Only Curious if there's a real session (started a Learn module)
  const isCurious = (hasSession && isCuriousStarted) && !isCuriousPlus;

  // Check if user has Kin roles
  const hasKinRole = availableRoles?.some(r => r === 'kin_member' || r === 'kin_coordinator');
  const isKinActive = activeRole === 'kin_member' || activeRole === 'kin_coordinator';

  useEffect(() => {
    if (user && user._id) {
      fetchProgress();
    }
  }, [user]);

  useEffect(() => {
    const handleStatusChange = () => {
      fetchProgress();
    };
    window.addEventListener('curiousStatusChanged', handleStatusChange);
    return () => {
      window.removeEventListener('curiousStatusChanged', handleStatusChange);
    };
  }, []);

  // If not logged in and no Curious activity, render nothing
  // The Navbar will show Login / Sign Up instead
  // NOTE: This check is AFTER all hooks so React's rules-of-hooks are respected
  if (!isLoggedIn && !isCurious && !isCuriousPlus) {
    return null;
  }

  // Show loading until data is ready
  const shouldShowLoading = (isCurious || isLoggedIn) && !dataReady;

  if (shouldShowLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5">
        <span className="text-sm text-white">⏳</span>
      </div>
    );
  }

  const hasMultipleRoles = availableRoles?.length > 1;
  // Use activeRole for display and menu, not user.role
  const currentRole = isLoggedIn ? activeRole : 'curious';

  let statusIcon = ROLE_ICONS[currentRole] || '🔍';
  let statusText = isLoggedIn ? user.name || ROLE_NAMES[currentRole] : 'Curious';
  let statusColor = 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100';

  if (isLoggedIn) {
    statusColor = 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100';
  } else if (isCuriousPlus) {
    statusIcon = '📧';
    statusText = 'Curious+';
    statusColor = 'bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100';
  } else if (isCurious) {
    statusIcon = '🔍';
    statusText = 'Curious';
    statusColor = 'bg-yellow-50 border-yellow-200 text-yellow-700 hover:bg-yellow-100';
  }

  // If user has Kin role and is active, show Kin badge
  if (isLoggedIn && hasKinRole && isKinActive) {
    statusIcon = activeRole === 'kin_coordinator' ? '🌟' : '👥';
    statusText = activeRole === 'kin_coordinator' ? 'Kin Coordinator' : 'Kin Member';
  }

  const handleLogout = async () => {
    setIsOpen(false);
    await logout();
    navigate('/');
  };

  const handleRoleSwitch = (role) => {
    setIsOpen(false);
    setActiveRole(role);
    localStorage.setItem('selectedRole', role);
    if (switchRole) {
      switchRole(role);
    }
    // Use the correct dashboard path for Kin Coordinator
    const path = role === 'kin_coordinator' ? '/kin-dashboard' : DASHBOARD_PATHS[role] || '/buyer/dashboard';
    navigate(path);
  };

  const handleClaim = () => {
    setIsOpen(false);
    const sessionId = getSessionId();
    if (localStorage.getItem('curious_email')) {
      navigate(`/claim-credits?session=${sessionId}`);
    } else {
      navigate(`/register?session=${sessionId}`);
    }
  };

  const handleGoToMessages = () => {
    setIsOpen(false);
    // Navigate to buyer dashboard with inbox tab open
    navigate('/buyer/dashboard?tab=inbox');
  };

  // Use activeRole to determine which menu to show
  const MenuComponent = isLoggedIn ? MenuComponents[currentRole] || MenuComponents.buyer : null;

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border transition-colors relative ${statusColor}`}
      >
        <span>{statusIcon}</span>
        <span>{statusText}</span>
        {hasMultipleRoles && <span className="text-xs text-gray-400">▼</span>}
        {credits > 0 && (
          <span className="text-xs bg-green-200 text-green-700 px-1.5 py-0.5 rounded-full">
            💰{credits.toFixed(2)}
          </span>
        )}
        {modulesCompleted > 0 && (
          <span className="text-xs bg-blue-200 text-blue-700 px-1.5 py-0.5 rounded-full">
            📚{modulesCompleted}
          </span>
        )}
        {/* Message badge - overlay on the user status button */}
        {isLoggedIn && unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-md">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-200 p-4 z-50 max-h-[80vh] overflow-y-auto">
          
          {/* SECTION 1: User Info */}
          <div className="border-b border-gray-100 pb-3 mb-3">
            <p className="font-medium text-sm">
              {isLoggedIn 
                ? `${statusIcon} ${user.name}` 
                : isCuriousPlus 
                  ? '📧 Curious+ Explorer' 
                  : isCurious 
                    ? '🔍 Curious Explorer' 
                    : '🔍 Exploring'}
            </p>
            {!isLoggedIn && isCurious && (
              <p className="text-xs text-yellow-600 mt-0.5">
                {isCuriousPlus ? 'You are ready to upgrade!' : 'Complete a course to earn credits!'}
              </p>
            )}
            {/* Kin badge in user info */}
            {isLoggedIn && hasKinRole && isKinActive && (
              <p className="text-xs text-purple-600 mt-0.5">
                {activeRole === 'kin_coordinator' ? '🌟 Kin Coordinator' : '👥 Kin Member'}
              </p>
            )}
            {/* Messages quick link */}
            {isLoggedIn && (
              <button
                onClick={handleGoToMessages}
                className="mt-2 w-full text-left px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded transition flex items-center gap-2"
              >
                <span>📬</span>
                <span>Messages</span>
                {unreadCount > 0 && (
                  <span className="ml-auto bg-red-500 text-white text-xs font-bold rounded-full px-2 py-0.5">
                    {unreadCount}
                  </span>
                )}
              </button>
            )}
          </div>

          {/* SECTION 2: Curious Started Menu (in progress, not completed) */}
          {!isLoggedIn && isCurious && !isCuriousPlus && (
            <>
              <div className="border-b border-gray-100 pb-3 mb-3">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-2">🔍 You are Learning!</p>
                <p className="text-sm text-gray-600">Complete your first course to earn credits and unlock upgrade options.</p>
                <div className="mt-2 text-xs text-gray-500">
                  📚 In progress: Security & Privacy Course
                </div>
              </div>
              <div className="border-b border-gray-100 pb-3 mb-3">
                <button 
                  onClick={() => { setIsOpen(false); navigate('/learn/security-privacy-course'); }} 
                  className="w-full text-left px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded transition flex items-center gap-2"
                >
                  <span>🔒</span> Continue Security & Privacy Course
                </button>
              </div>
              <div className="space-y-1">
                <button 
                  onClick={() => { setIsOpen(false); navigate('/learn'); }} 
                  className="w-full text-left px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded transition flex items-center gap-2"
                >
                  <span>📚</span> Browse More Courses
                </button>
              </div>
            </>
          )}

          {/* SECTION 2b: Curious+ Menu (completed module, has email) */}
          {!isLoggedIn && isCuriousPlus && (
            <>
              {/* Progress Summary */}
              <div className="border-b border-gray-100 pb-3 mb-3">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-2">🌟 Your Progress</p>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">💰 Credits</span>
                    <span className="font-bold text-green-600">${credits.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">📚 Modules Completed</span>
                    <span className="font-bold text-blue-600">{modulesCompleted}</span>
                  </div>
                </div>
              </div>

              {/* Upgrade Options */}
              <div className="border-b border-gray-100 pb-3 mb-3">
                <UpgradeOptions 
                  onNavigate={(path) => { setIsOpen(false); navigate(path); }}
                  onClose={() => setIsOpen(false)}
                />
              </div>

              {/* Explore */}
              <div className="border-b border-gray-100 pb-3 mb-3">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-2">📚 Explore</p>
                <button onClick={() => { setIsOpen(false); navigate('/learn'); }} className="w-full text-left text-sm text-blue-600 hover:bg-blue-50 rounded px-3 py-1.5 transition">📚 Browse All Courses</button>
                <button onClick={() => { setIsOpen(false); navigate('/products'); }} className="w-full text-left text-sm text-blue-600 hover:bg-blue-50 rounded px-3 py-1.5 mt-1">🛒 Browse Products</button>
                <button onClick={() => { setIsOpen(false); navigate('/hubs'); }} className="w-full text-left text-sm text-blue-600 hover:bg-blue-50 rounded px-3 py-1.5 mt-1">🏪 Browse Hubs</button>
              </div>

              {/* Register */}
              <div className="space-y-1">
                <button onClick={handleClaim} className="w-full text-left px-3 py-1.5 text-sm text-green-600 hover:bg-green-50 rounded transition-colors flex items-center gap-2">
                  <span>📧</span> Register to Save Your Progress
                </button>
              </div>
            </>
          )}

          {/* SECTION 3: Role Menu Items (lazy loaded) */}
          {isLoggedIn && MenuComponent && (
            <div className="border-b border-gray-100 pb-3 mb-3">
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1">Menu</p>
              <Suspense fallback={<MenuLoader />}>
                <MenuComponent 
                  user={user} 
                  onNavigate={(path) => { setIsOpen(false); navigate(path); }}
                />
              </Suspense>
            </div>
          )}

          {/* SECTION 4: Role Switcher */}
          {hasMultipleRoles && (
            <div className="border-b border-gray-100 pb-3 mb-3">
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1">Switch Role</p>
              {availableRoles.map((role) => {
                const isActive = activeRole === role;
                return (
                  <button
                    key={role}
                    onClick={() => handleRoleSwitch(role)}
                    className={`w-full text-left px-3 py-1.5 rounded text-sm transition-colors flex items-center gap-2 ${
                      isActive ? 'bg-green-50 text-green-700 font-medium' : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <span>{ROLE_ICONS[role] || '🔹'}</span>
                    <span>{ROLE_NAMES[role] || role}</span>
                    {isActive && <span className="ml-auto text-green-600">✓</span>}
                  </button>
                );
              })}
            </div>
          )}

          {/* SECTION 5: Actions */}
          <div className="space-y-1">
            {isLoggedIn && user.isGuest && (
              <button onClick={() => { setIsOpen(false); navigate('/set-password'); }} className="w-full text-left px-3 py-1.5 text-sm text-yellow-600 hover:bg-yellow-50 rounded transition-colors flex items-center gap-2">
                <span>🔑</span> Set Password (faster login)
              </button>
            )}
            {!isLoggedIn && isCuriousPlus && (
              <button onClick={handleClaim} className="w-full text-left px-3 py-1.5 text-sm text-green-600 hover:bg-green-50 rounded transition-colors flex items-center gap-2">
                <span>📧</span> Complete Registration →
              </button>
            )}
            {!isLoggedIn && isCurious && !isCuriousPlus && (
              <button onClick={handleClaim} className="w-full text-left px-3 py-1.5 text-sm text-green-600 hover:bg-green-50 rounded transition-colors flex items-center gap-2">
                <span>📧</span> Save Progress →
              </button>
            )}
            {!isLoggedIn && !isCurious && (
              <button onClick={handleClaim} className="w-full text-left px-3 py-1.5 text-sm text-green-600 hover:bg-green-50 rounded transition-colors flex items-center gap-2">
                <span>📧</span> Start Learning →
              </button>
            )}
            {isLoggedIn && (
              <button onClick={handleLogout} className="w-full text-left px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 rounded transition-colors flex items-center gap-2">
                <span>🚪</span> Logout
              </button>
            )}
          </div>

        </div>
      )}
    </div>
  );
};

export default UserStatus;
